import { useState } from "react";

import {
  aed,
  COLOURS,
  EPOXY_FINISHES,
  extraValue,
  FINISH_LABEL,
  isEpoxyFinish,
  listPriceFor,
  SYSTEM_LABEL,
  SYSTEMS,
  type ExtraOptionRow,
  type Finish,
  type OrderExtra,
  type OrderSection,
  type OrderSettingsRow,
  type PriceRow,
  type System,
} from "@/lib/orders";
import { AreaCalculatorToggle } from "./AreaCalculator";
import { Card, Label, Toggle } from "./ui";

/**
 * One sold surface (floor or wall): finish pills with the list price, colour,
 * m², editable unit price, this section's own extras, notes.
 */
export function SectionCard({
  tag,
  kind,
  section,
  onChange,
  priceList,
  extraOptions,
  settings,
  disabled,
  base,
  extrasAmt,
  fixedPrice = null,
}: {
  tag: string;
  kind: "floor" | "wall";
  section: OrderSection | null;
  onChange: (s: OrderSection | null) => void;
  priceList: PriceRow[];
  extraOptions: ExtraOptionRow[];
  settings: OrderSettingsRow;
  disabled: boolean;
  base: number;
  extrasAmt: number;
  /** Package price (bathroom packages): replaces m² × rate. */
  fixedPrice?: number | null;
}) {
  const [priceTouched, setPriceTouched] = useState(false);
  const title = kind === "floor" ? "Floor" : "Wall";
  const system: System = section?.system ?? "microcement";
  const listPrice = (finish: Finish, sys: System = system) => listPriceFor(settings, priceList, sys, finish, kind);
  const firstFinish = (sys: System): Finish | null => (sys === "epoxy" ? "epoxy_chromatic" : ((priceList.find((p) => p.active) ?? priceList[0])?.finish ?? null));

  const enable = () => {
    const finish = firstFinish("microcement");
    if (!finish) return;
    onChange({ system: "microcement", finish, sqm: 0, unitPrice: fixedPrice ?? listPrice(finish, "microcement"), extras: [], colour: "" });
    setPriceTouched(false);
  };

  const patch = (p: Partial<OrderSection>) => section && onChange({ ...section, ...p });

  const pickFinish = (finish: Finish) => {
    if (!section) return;
    onChange({ ...section, finish, unitPrice: fixedPrice ?? (priceTouched ? section.unitPrice : listPrice(finish)) });
  };

  /** Changing the system re-picks a finish of that system and its list price. */
  const pickSystem = (sys: System) => {
    if (!section || sys === section.system) return;
    const keep = sys !== "epoxy" && !isEpoxyFinish(section.finish) ? section.finish : firstFinish(sys);
    if (!keep) return;
    onChange({ ...section, system: sys, finish: keep, unitPrice: fixedPrice ?? listPrice(keep, sys) });
    setPriceTouched(false);
  };

  const toggleExtra = (opt: ExtraOptionRow) => {
    if (!section) return;
    const has = section.extras.some((e) => e.code === opt.code);
    const extras = has
      ? section.extras.filter((e) => e.code !== opt.code)
      : [...section.extras, { code: opt.code, label: opt.label, kind: opt.kind, pct: opt.pct ?? undefined }];
    onChange({ ...section, extras });
  };

  const other = section?.extras.find((e) => e.code === "other") ?? null;
  const setOther = (o: OrderExtra | null) => {
    if (!section) return;
    const rest = section.extras.filter((e) => e.code !== "other");
    onChange({ ...section, extras: o ? [...rest, o] : rest });
  };

  return (
    <Card
      tag={tag}
      right={
        <label className="flex items-center gap-2 rounded-full border border-white/15 bg-night/80 px-3 py-1 text-[0.62rem] uppercase tracking-[0.14em] text-ice/75">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 accent-[#D6338F]"
            disabled={disabled}
            checked={Boolean(section)}
            onChange={(e) => (e.target.checked ? enable() : onChange(null))}
          />
          {title} in this order
        </label>
      }
    >
      {!section ? (
        <p className="text-sm italic text-ice/50">— not included —</p>
      ) : (
        <div className="space-y-4">
          <div>
            <Label>System</Label>
            <div className="flex flex-wrap gap-2">
              {SYSTEMS.map((sys) => (
                <Toggle
                  key={sys}
                  on={system === sys}
                  label={SYSTEM_LABEL[sys]}
                  value={sys === "geopolymer" ? `+${settings.geopolymerPct}%` : undefined}
                  disabled={disabled}
                  onClick={() => pickSystem(sys)}
                />
              ))}
            </div>
          </div>

          <div>
            <Label>{system === "epoxy" ? "Epoxy type" : "Finish"}{fixedPrice == null ? ` · AED / m² (${title.toLowerCase()} list price)` : " · included in the package"}</Label>
            <div className="flex flex-wrap gap-2">
              {system === "epoxy"
                ? EPOXY_FINISHES.map((e) => (
                    <Toggle
                      key={e.finish}
                      on={section.finish === e.finish}
                      label={e.label}
                      value={fixedPrice == null ? aed(listPrice(e.finish)).replace(/\.00$/, "") : undefined}
                      disabled={disabled}
                      onClick={() => pickFinish(e.finish)}
                    />
                  ))
                : priceList
                    .filter((p) => p.active || p.finish === section.finish)
                    .map((p) => (
                      <Toggle
                        key={p.finish}
                        on={section.finish === p.finish}
                        label={p.label || FINISH_LABEL[p.finish]}
                        value={fixedPrice == null ? aed(listPrice(p.finish)).replace(/\.00$/, "") : undefined}
                        disabled={disabled}
                        onClick={() => pickFinish(p.finish)}
                      />
                    ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block">
              <Label>Colour</Label>
              <select className="field" disabled={disabled} value={section.colour ?? ""} onChange={(e) => patch({ colour: e.target.value })}>
                <option value="" className="bg-night">to be chosen</option>
                {COLOURS.map((c) => (
                  <option key={c} value={c} className="bg-night">{c}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <Label>{fixedPrice == null ? "Area m²" : "Approx. m² (optional)"}</Label>
              <input
                className="field num"
                type="number"
                inputMode="decimal"
                min={0}
                step="0.5"
                dir="ltr"
                disabled={disabled}
                value={section.sqm || ""}
                onChange={(e) => patch({ sqm: Number(e.target.value) })}
              />
            </label>
            {fixedPrice == null ? (
              <label className="block">
                <Label>Price / m² AED {!priceTouched && <span className="normal-case tracking-normal text-ice/45">(list)</span>}</Label>
                <input
                  className="field num"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.5"
                  dir="ltr"
                  disabled={disabled}
                  value={section.unitPrice}
                  onChange={(e) => {
                    setPriceTouched(true);
                    patch({ unitPrice: Number(e.target.value) });
                  }}
                />
              </label>
            ) : (
              <div className="block">
                <Label>Package price</Label>
                <div className="field num flex items-center text-goldbright">{aed(fixedPrice)}</div>
              </div>
            )}
          </div>

          <div className="-mt-2">
            <AreaCalculatorToggle kind={kind} disabled={disabled} onApply={(sqm) => patch({ sqm })} />
          </div>

          <div>
            <Label>Extras for the {title.toLowerCase()}</Label>
            <div className="flex flex-wrap gap-2">
              {extraOptions
                .filter((o) => o.active || section.extras.some((e) => e.code === o.code))
                .map((o) => (
                  <Toggle
                    key={o.code}
                    tone="gold"
                    on={section.extras.some((e) => e.code === o.code)}
                    label={o.label}
                    value={extraValue(o)}
                    disabled={disabled}
                    onClick={() => toggleExtra(o)}
                  />
                ))}
              <Toggle
                tone="gold"
                on={Boolean(other)}
                label="Other"
                disabled={disabled}
                onClick={() => setOther(other ? null : { code: "other", label: "", kind: "percent", pct: 0 })}
              />
            </div>
            {other && (
              <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto_8rem]">
                <input
                  className="field"
                  placeholder="What is it? (e.g. stairs, niches)"
                  disabled={disabled}
                  value={other.label}
                  onChange={(e) => setOther({ ...other, label: e.target.value })}
                />
                <div className="flex gap-1 rounded-full border border-white/15 p-0.5">
                  {(["percent", "fixed"] as const).map((k) => (
                    <button
                      key={k}
                      type="button"
                      disabled={disabled}
                      onClick={() => setOther({ code: "other", label: other.label, kind: k, pct: k === "percent" ? other.pct ?? 0 : undefined, amount: k === "fixed" ? other.amount ?? 0 : undefined })}
                      className={`rounded-full px-3 py-1 text-[0.62rem] font-semibold uppercase tracking-[0.14em] ${other.kind === k ? "bg-white/15 text-paper" : "text-ice/55"}`}
                    >
                      {k === "percent" ? "%" : "AED"}
                    </button>
                  ))}
                </div>
                <input
                  className="field num"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  dir="ltr"
                  disabled={disabled}
                  value={other.kind === "percent" ? other.pct ?? 0 : other.amount ?? 0}
                  onChange={(e) =>
                    setOther(
                      other.kind === "percent"
                        ? { ...other, pct: Number(e.target.value) }
                        : { ...other, amount: Number(e.target.value) },
                    )
                  }
                />
              </div>
            )}
          </div>

          <label className="block">
            <Label>Notes · rooms, levels, special details</Label>
            <textarea className="field min-h-14" disabled={disabled} value={section.notes ?? ""} onChange={(e) => patch({ notes: e.target.value })} />
          </label>

          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 border-t border-white/10 pt-3 text-sm">
            <span className="text-ice/60">{fixedPrice == null ? "Base" : "Package"} <b className="num text-paper">{aed(base)}</b></span>
            <span className="text-ice/60">Extras <b className="num text-paper">{aed(extrasAmt)}</b></span>
            <span className="ml-auto text-ice/60">{title} total <b className="num text-lg text-goldbright">{aed(base + extrasAmt)}</b></span>
          </div>
        </div>
      )}
    </Card>
  );
}
